import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, YAxis } from "recharts";
import { api } from "../lib/api";
import { useMarket } from "../lib/store";
import { useSubscribe, useTick } from "../lib/socket";
import { usePortfolio } from "../lib/usePortfolio";
import { inr, pct, signed, tone, fmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Price } from "../components/Price";
import { Panel, Metric } from "../components/Panel";
import { TickerTape, RiskGauge, TargetProgress, CHALLENGE_MODE, SectorHeatmap, EquityCurve, Spotlight } from "../components/motion";
import { useWatchlists } from "../lib/watchlists";
import { Layers, LineChart, FileBarChart, Bell } from "lucide-react";
import { TgWidget } from "../components/community/TgBits";
import { DiwaliPill } from "../components/diwali/DiwaliOffer";

const SHORTCUTS = [["/option-chain", "Option chain", "Live OI, IV and Greeks", Layers], ["/strategy", "Strategy builder", "Payoff before you trade", LineChart], ["/reports", "Reports", "P&L, tradebook, CSV", FileBarChart], ["/gtt", "Alerts & GTT", "Targets and stop-losses", Bell]];

function Shortcuts() {
  const nav = useNavigate();
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-shortcuts">
      {SHORTCUTS.map(([to, t, s, Icon]) => (
        <Spotlight key={to} className="rounded-xl border border-border bg-card">
          <button onClick={() => nav(to)} data-testid={`shortcut-${to.slice(1)}`} className="relative z-[2] w-full text-left p-4 flex items-start gap-3">
            <Icon className="h-4 w-4 text-primary mt-0.5" /><span><span className="block text-[13px] font-semibold">{t}</span><span className="block text-[11px] text-muted-foreground">{s}</span></span>
          </button>
        </Spotlight>))}
    </div>
  );
}

const INDICES = [["NIFTY 50", "NSE_INDEX|Nifty 50"], ["SENSEX", "BSE_INDEX|SENSEX"], ["BANK NIFTY", "NSE_INDEX|Nifty Bank"], ["FIN NIFTY", "NSE_INDEX|Nifty Fin Service"]];

function IndexCard({ name, k, i }) {
  const t = useTick(k);
  const nav = useNavigate();
  const [series, setSeries] = useState([]);
  useEffect(() => {
    api.get("/candles", { params: { key: k, tf: "5m" } }).then((r) => setSeries(r.data.candles.slice(-75))).catch(() => {});
  }, [k]);
  const up = (t?.ch ?? 0) >= 0;
  const color = up ? "#1FBF8F" : "#F0505A";
  return (
    <button onClick={() => nav(`/instrument/${encodeURIComponent(k)}`)} data-testid={`dashboard-index-${name.replace(/\s/g, "-")}`}
      className="rise gold-hairline group text-left rounded-xl border border-border bg-card panel-sheen p-4 hover:border-primary/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-300" style={{ animationDelay: `${i * 60}ms` }}>
      <div className="eyebrow">{name}</div>
      <div className="flex items-baseline gap-2 mt-1.5">
        <span className="text-[22px] font-semibold tracking-tight"><Price value={t?.ltp} prev={t?.prevLtp} arrow /></span>
      </div>
      <div className={`num text-[11.5px] ${tone(t?.ch)}`}>{signed(t?.ch)} ({pct(t?.chp)})</div>
      <div className="h-12 mt-2 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series}>
            <defs><linearGradient id={`g${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Area type="monotone" dataKey="c" stroke={color} strokeWidth={1.5} fill={`url(#g${i})`} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </button>
  );
}

function Movers({ basket }) {
  const ticks = useMarket((s) => s.ticks);
  const meta = useMarket((s) => s.meta);
  const openOrder = useMarket((s) => s.openOrder);
  const rows = basket.map((k) => ({ k, t: ticks[k], m: meta[k] })).filter((r) => r.t?.chp != null);
  rows.sort((a, b) => b.t.chp - a.t.chp);
  const Table = ({ list, title, testid }) => (
    <Panel title={title} testid={testid}>
      <div className="divide-y divide-border/60">
        {list.map(({ k, t, m }) => (
          <div key={k} className="flex items-center justify-between px-4 h-9 hover:bg-accent/50 group">
            <span className="text-[12.5px] font-medium">{m?.trading_symbol || k}</span>
            <span className="flex items-center gap-3">
              <span className="num text-[12px]">{fmt(t.ltp)}</span>
              <span className={`num text-[11.5px] w-16 text-right ${tone(t.chp)}`}>{pct(t.chp)}</span>
              <button onClick={() => openOrder({ key: k, side: "BUY" })} className="opacity-0 group-hover:opacity-100 h-5 px-1.5 rounded bg-up text-white text-[10px] font-bold">B</button>
            </span>
          </div>
        ))}
        {list.length === 0 && <div className="px-4 py-6 text-[12px] text-muted-foreground">Waiting for ticks…</div>}
      </div>
    </Panel>
  );
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Table title="Top gainers · NIFTY basket" list={rows.slice(0, 6)} testid="top-gainers" />
      <Table title="Top losers · NIFTY basket" list={rows.slice(-6).reverse()} testid="top-losers" />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const funds = usePortfolio("/funds", 5000);
  const holdings = usePortfolio("/holdings");
  const ticks = useMarket((s) => s.ticks);
  const [basket, setBasket] = useState([]);
  useEffect(() => { api.get("/instruments/indices").then((r) => setBasket(r.data.basket)); }, []);
  useSubscribe([...INDICES.map((x) => x[1]), ...basket, ...(holdings || []).map((h) => h.instrument_key)]);
  const hv = (holdings || []).reduce((a, h) => a + h.qty * (ticks[h.instrument_key]?.ltp ?? h.avg_price), 0);
  const dayPnl = funds ? funds.realised_pnl_today + funds.unrealised_mtm : null;
  const wl = useWatchlists((s) => s.lists.find((l) => l.index === s.active)?.items);
  const tape = [...INDICES.map((x) => x[1]), ...(wl || []).map((x) => (typeof x === "string" ? x : x.instrument_key || x.key)).filter(Boolean)];
  useSubscribe(tape);
  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1400px]" data-testid="dashboard-page">
      <div className="-mt-4 lg:-mt-6"><TickerTape keys={tape} /></div>
      <div className="rise">
        <div className="eyebrow">Virtual account</div>
        <h1 className="font-display font-bold text-2xl mt-1">Hi {user?.name?.split(" ")[0]}, here's your market.</h1>
        <div className="mt-3 max-w-[560px] empty:hidden"><DiwaliPill /></div>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {INDICES.map(([n, k], i) => <IndexCard key={k} name={n} k={k} i={i} />)}
      </div>
      <TgWidget className="md:hidden" />
      <Panel title="Account summary" testid="account-summary">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          <Metric label="Available margin" value={inr(funds?.available_margin)} sub={`Cash ${inr(funds?.cash_balance)}`} testid="summary-available-margin" />
          <Metric label="Used margin" value={inr(funds?.used_margin)} sub={`Orders ${inr(funds?.order_margin)}`} testid="summary-used-margin" />
          <Metric label="Today's P&L" value={<span className={tone(dayPnl)}>{signed(dayPnl)}</span>} sub={`Charges ${inr(funds?.charges_today)}`} testid="summary-today-pnl" />
          <Metric label="Holdings value" value={inr(hv)} sub={`${(holdings || []).length} stocks`} testid="summary-holdings-value" />
        </div>
      </Panel>
      <Shortcuts />
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-4">
        <Panel title="Risk · margin used" testid="dashboard-risk-card"><RiskGauge used={funds?.used_margin || 0} total={(funds?.used_margin || 0) + (funds?.available_margin || 0)} /></Panel>
        <Panel title="Performance · account equity" testid="dashboard-performance-card"><EquityCurve days={90} height={150} /></Panel>
      </div>
      {CHALLENGE_MODE && <Panel title="Account objectives" testid="dashboard-objectives"><TargetProgress value={dayPnl || 0} target={1} /></Panel>}
      <Panel title="Market overview · sectors" testid="dashboard-market-overview"><SectorHeatmap /></Panel>
      <Movers basket={basket} />
    </div>
  );
}
